import LibraryAddCheckOutlinedIcon from '@mui/icons-material/LibraryAddCheckOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import { Box, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { BiFilterAlt } from 'react-icons/bi';
import { ILeadSearchFilter, getAllLeadStatusByVisibility } from 'wisestep-api';
import {
  SearchBarNew,
  WsButton,
  WsColors,
  WsSelect,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { getFilterValue } from '../../../features/notes/helper/NotesHelper';
import SmartFilter from '../../../features/submissionsNew/components/filters/smart/SmartFilter';
import { defaultLeadSearchFilter } from '../helper/leadHelper';

import { StatusList } from './LeadDetailsForm';

export interface ILeadsFilters {
  onAdvancedFiltersClick: () => void;
  searchQuery: ILeadSearchFilter;
  clear: () => void;
  getUpdatedFiltersFromFilters: (data) => void;
  anyLeadsSelected: boolean;
  onRandomAssign: () => void;
  onBulkAssign: () => void;
  selectedLeadsCount?: number;
  onAddLead: () => void;
}

const LeadFilters = ({
  onAdvancedFiltersClick,
  searchQuery,
  getUpdatedFiltersFromFilters,
  anyLeadsSelected,
  onRandomAssign,
  onBulkAssign,
  selectedLeadsCount,
}: ILeadsFilters) => {
  const methods = useForm({
    defaultValues: searchQuery ?? defaultLeadSearchFilter,
  });
  const { getValues, setValue } = methods;
  const onSearch = () => handleFilters();
  const [statusList, setStatusList] = useState<StatusList[]>([]);

  const handleFilters = useCallback(async () => {
    const updatedQuery = searchQuery;
    updatedQuery.query = getValues('query') ?? '';
    updatedQuery.leadOwners = getValues('leadOwners') ?? [];
    updatedQuery.status = getValues('status') ?? [];
    updatedQuery.createdByIds = getValues('createdByIds') ?? [];
    getUpdatedFiltersFromFilters({ ...updatedQuery });
  }, [searchQuery, getUpdatedFiltersFromFilters, getValues]);

  const onClear = () => {
    setStatusList([]);
    setValue('status', []);
    const updatedQuery = searchQuery;
    updatedQuery.query = '';
    updatedQuery.leadOwners = [];
    updatedQuery.status = [];
    updatedQuery.createdByIds = [];
    getUpdatedFiltersFromFilters({ ...updatedQuery });
  };

  useEffect(() => {
    const getStatusList = async () => {
      const response = await getAllLeadStatusByVisibility();
      const statusData: StatusList[] = [];
      response.data?.forEach((statusObj) => {
        statusData.push({
          label: statusObj.leadStatusName as string,
          value: statusObj.id as string,
        });
      });
      setStatusList(statusData);
    };
    getStatusList();
  }, []);

  return (
    <Box
      bgcolor={WsColors.White}
      sx={{
        padding: '16px',
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSearch)}>
          <Grid container bgcolor={WsColors.White}>
            <Grid container justifyContent="flex-start" spacing={1} xs={7}>
              <Grid item xs={8} sx={{ my: 2, ml: 2 }}>
                <FormProvider {...methods}>
                  <SearchBarNew size="small" onSearch={onSearch} name="query" />
                </FormProvider>
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" alignItems="center" xs={5} spacing={1}>
              {anyLeadsSelected ? (
                <>
                  <Grid item>
                    <WsTypography color={WsTypoColors.PRIMARY600} bold>
                      {selectedLeadsCount} selected
                    </WsTypography>
                  </Grid>
                  <Grid item>
                    <WsButton
                      variant="contained"
                      size="medium"
                      disabled={selectedLeadsCount === 0}
                      onClick={onBulkAssign}
                      startIcon={<PersonAddAlt1OutlinedIcon />}
                    >
                      Assign Lead Owner
                    </WsButton>
                  </Grid>
                  <Grid item>
                    <WsButton
                      variant="contained"
                      size="medium"
                      startIcon={<LibraryAddCheckOutlinedIcon />}
                      onClick={onRandomAssign}
                    >
                      Assign Randomly
                    </WsButton>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item>
                    <WsButton
                      variant="outlined"
                      size="medium"
                      onClick={onAdvancedFiltersClick}
                      startIcon={<BiFilterAlt />}
                    >
                      Advanced filters
                    </WsButton>
                  </Grid>
                </>
              )}
            </Grid>
            <Grid container justifyContent="flex-start" alignItems="center" pt={2} spacing={1}>
              <Grid item>
                <WsTypography variant={WsTypoVariants.SUB_TITLE} bold color={WsTypoColors.BLACK}>
                  Applied Filter:
                </WsTypography>
              </Grid>
              <Grid item>
                <SmartFilter
                  filterName="Status"
                  onClick={onSearch}
                  filterValue={getFilterValue('status', methods)}
                  onClear={onClear}
                >
                  <WsSelect
                    name="status"
                    selectArray={statusList}
                    placeholder="Select Lead Status"
                    required
                  />
                </SmartFilter>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Box>
  );
};
export default LeadFilters;
